/**
 * Calculates the perceived brightness of a hex color.
 * Uses the luminosity formula: (R * 299 + G * 587 + B * 114) / 1000
 *
 * @param color - Hex color string (e.g., "#FF5733" or "#fff")
 * @returns Brightness value from 0 (darkest) to 255 (brightest)
 */
export function getHexColorBrightness(color: string): number {
  let hex = color.replace("#", "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  return (r * 299 + g * 587 + b * 114) / 1000;
}

/**
 * Returns black or white color based on the brightness of the background color.
 * Useful for ensuring text contrast and accessibility.
 *
 * @param backgroundColor - Hex color string (e.g., "#FF5733" or "#fff")
 * @param threshold - Brightness threshold (default: 128, range: 0-255)
 * @returns "#000000" for light backgrounds, "#FFFFFF" for dark backgrounds
 */
export function getContrastTextColor(
  backgroundColor: string,
  threshold: number = 158
): "#000000" | "#FFFFFF" {
  const brightness = getHexColorBrightness(backgroundColor);
  return brightness > threshold ? "#000000" : "#FFFFFF";
}
